/**
 * Canonical covenant type labels — names only (no explanations).
 *
 * Source of truth for the 35-type taxonomy lives in
 * `landing/lib/covenant-context.ts` alongside the full explanation +
 * impact + considerations strings used by the free-tier extraction
 * page. Here we mirror just the display labels so the dashboard can
 * render them without pulling the whole 400-line lookup table into
 * the SPA bundle.
 *
 * Partial-ship policy (per handoff T-102): if the extractor returns a
 * covenant type that doesn't map to a canonical key, we render the raw
 * label as-is rather than dropping the row.
 */

export const FINANCIAL_COVENANT_LABELS: Record<string, string> = {
  leverage_ratio: 'Leverage ratio',
  debt_to_equity: 'Debt-to-equity',
  senior_leverage_ratio: 'Senior leverage ratio',
  net_debt: 'Net debt',
  interest_coverage_ratio: 'Interest coverage (ICR)',
  debt_service_coverage_ratio: 'Debt service coverage (DSCR)',
  fixed_charge_coverage_ratio: 'Fixed charge coverage (FCCR)',
  minimum_ebitda: 'Minimum EBITDA',
  minimum_working_capital: 'Minimum working capital',
  current_ratio: 'Current ratio',
  asset_coverage_ratio: 'Asset coverage ratio',
  cash_flow_covenant: 'Cash flow covenant',
  accounts_receivable_aging: 'Accounts receivable aging',
  tangible_net_worth: 'Tangible net worth',
  solvency_ratio: 'Solvency ratio',
};

export const OPERATIONAL_COVENANT_LABELS: Record<string, string> = {
  dividend_restriction: 'Dividend restriction',
  additional_debt_restriction: 'Additional debt restriction',
  capex_limitation: 'CapEx limitation',
  acquisition_restriction: 'Acquisition restriction',
  asset_disposal_restriction: 'Asset disposal restriction',
  change_of_control: 'Change of control',
  insurance_maintenance: 'Insurance maintenance',
  reporting_requirement: 'Reporting requirement',
  affiliate_transaction_restriction: 'Affiliate transaction restriction',
  business_scope_restriction: 'Business scope restriction',
  tax_compliance: 'Tax compliance',
  collateral_maintenance: 'Collateral maintenance',
  negative_pledge: 'Negative pledge',
  pari_passu: 'Pari passu',
  key_person: 'Key person clause',
  mandatory_prepayment: 'Mandatory prepayment',
  payment_routing: 'Payment routing',
  hedging_requirement: 'Hedging requirement',
  mac_clause: 'Material adverse change',
  subordination_requirement: 'Subordination requirement',
};

const ALL_LABELS: Record<string, string> = {
  ...FINANCIAL_COVENANT_LABELS,
  ...OPERATIONAL_COVENANT_LABELS,
};

/**
 * Normalise a raw extractor label to the canonical display name.
 *
 * Tries multiple heuristics:
 *   1. exact lowercase + snake_case key match
 *   2. space → underscore + lowercase
 *   3. fallback to the raw input (partial-ship policy)
 */
export function covenantDisplayName(raw: string | null | undefined): string {
  if (!raw) {return '';}
  const trimmed = raw.trim();
  if (!trimmed) {return '';}

  const snake = trimmed.toLowerCase().replace(/[\s-]+/g, '_');
  if (ALL_LABELS[snake]) {return ALL_LABELS[snake];}

  const singular = snake.replace(/s$/, '');
  if (ALL_LABELS[singular]) {return ALL_LABELS[singular];}

  return trimmed;
}
